import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AccordionModule } from 'primeng/accordion';
import { MessageService, TreeNode } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { successMsg, warningMsg } from '../../../../common/messages';
import { catalogContainerInsectSort, catalogContainerSubstrateSort, containerSort, dictionaryEntrySort } from '../../../../common/sorts';

import { AuditInfoComponent, CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  CatalogAdminService,
  ContainerAdminService,
  DictionaryCacheService,
  ICatalogContainerInsectGrowthStageView,
  ICatalogContainerSubstrateView,
  IContainer,
  IContainerInsectGrowthStageUpdateRequest,
  IContainerSubstrateUpdateRequest,
  IDictionaryEntry
} from '../../../../services/insecta';

@Component({
  selector: 'tab-container',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AccordionModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    AuditInfoComponent
  ],

  templateUrl: 'tab-containers.component.html'
})
export class TabContainerComponent implements OnDestroy, OnInit, CanComponentDeactivate {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;

  pageVars: any = {
    isEditing: false,
    isReady: false,
    isLoading: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };

  @Input() currentUser: string = ``;
  @Input() catalogId: string = this.emptyGuid;
  @Output('isReady') isReady = new EventEmitter<boolean>();
  isAvailable: boolean = true;

  containers: IContainer[] = [];
  unitsOfMeasure: IDictionaryEntry[] = [];

  selectedContainer?: IContainer;
  insectGrowthStages: ICatalogContainerInsectGrowthStageView[] = [];
  insectGrowthStagesNodes: TreeNode[] = [];
  substrates: ICatalogContainerSubstrateView[] = [];

  constructor(
    private appInsights: AppInsightsService,
    private catalogService: CatalogAdminService,
    private containerService: ContainerAdminService,
    private dictionaryCache: DictionaryCacheService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:ContainersTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabcontainers`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
    this.selectedContainer = undefined;
  }

  ngOnInit() {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = false;

    const request = {
      containers: this.containerService.getContainers(),
      unitOfMeasure: this.dictionaryCache.get('volume')
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.containers = response.containers.sort(containerSort);
        this.unitsOfMeasure = response.unitOfMeasure.sort(dictionaryEntrySort);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabContainerComponent', action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
        this.isReady.emit(true);
      });
  }

  containerSelected() {
    this.loadCatalogContainerInfo();
  }

  containerUnselected() {
    this.insectGrowthStages = [];
    this.insectGrowthStagesNodes = [];
  }

  loadCatalogContainerInfo() {
    const request = {
      insectGrowthStages: this.catalogService.getCatalogContainerInsectGrowthStages(this.catalogId, <string>this.selectedContainer?.id),
      substrates: this.catalogService.getCatalogContainerSubstrates(this.catalogId, <string>this.selectedContainer?.id)
    };

    this.appInsights.trackEvent(`${this.pageVars.pageId}:SelectedContainer`, {
      catalogId: this.catalogId,
      containerId: <string>this.selectedContainer?.id
    });
    this.pageVars.isLoading = true;
    this.isReady.emit(false);

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.insectGrowthStagesNodes = this.buildInsectGrowthStageTree(response.insectGrowthStages.sort(catalogContainerInsectSort));
        this.substrates = response.substrates.sort(catalogContainerSubstrateSort);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabContainerComponent', action: 'SelectedContainer:Load:Error' });
      })
      .finally(() => {
        this.pageVars.isLoading = false;
        this.isReady.emit(true);
      });
  }

  buildInsectGrowthStageTree(items: ICatalogContainerInsectGrowthStageView[]): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    items
      .sort()

      .forEach((item) => {
        let parent = treeNodes.find((x) => x.key === item.insectId);
        if (parent === undefined) {
          parent = <TreeNode>{
            key: item.insectId,
            label: item.scientificName,
            data: { nodeType: 'parent', item: item },
            children: []
          };
          treeNodes.push(parent);
        }
        const child = <TreeNode>{
          key: item.id,
          label: item.growthStageName,
          data: { nodeType: 'child', item: item }
        };
        parent.children?.push(child);
      });

    return treeNodes.sort((a, b) => (a.label! > b.label! ? 1 : -1));
  }

  insectGrowthStageUpdate(item: ICatalogContainerInsectGrowthStageView) {
    const request = <IContainerInsectGrowthStageUpdateRequest>{
      id: item.id,
      infestationRate: item.infestationRate,
      isAvailable: item.isAvailable,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogContainerInsectGrowthStage(this.catalogId, request))
      .then(() => {
        const parent = this.insectGrowthStagesNodes.find((x) => x.key === item.insectId);
        if (parent) {
          const child = parent.children?.find((x) => x.key === item.id);
          if (child) {
            child.data.item.infestationRate = item.infestationRate;
            child.data.item.isAvaialble = item.isAvailable;
            child.data.item.action = 'UPD';
            child.data.item.actionBy = this.currentUser;
            child.data.item.actionOn = new Date();
          }
        }

        successMsg(this.msgService, 'Catalog Container/Insect has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabContainerComponent', action: 'InsectGrowthStage:Update:Error' });
      });
  }

  substrateUpdate(item: ICatalogContainerSubstrateView) {
    if (item.standardVolume != null && item.standardVolumeUom == null) {
      warningMsg(this.msgService, `Please define Standard Volume Unit of Measure to save Standard Volume`);
      return;
    }

    if (item.standardVolumeUom != null && item.standardVolume == null) {
      warningMsg(this.msgService, `Please define Standard Volume Amount to save Standard Volume`);
      return;
    }

    const request = <IContainerSubstrateUpdateRequest>{
      id: item.id,
      standardVolume: item.standardVolume,
      standardVolumeUom: item.standardVolumeUom,
      isAvailable: item.isAvailable,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogContainerSubstrate(this.catalogId, request))
      .then(() => {
        successMsg(this.msgService, 'Catalog Container/Insect has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabContainerComponent', action: 'Substrate:Update:Error' });
      });
  }

  resetComponent() {
    this.selectedContainer = undefined;
    this.insectGrowthStages = [];
    this.insectGrowthStagesNodes = [];
    this.substrates = [];
  }
}
